import React, { useMemo, useRef, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { t } from '@lingui/core/macro';
import './AssetManagement.css';
import { CollapsibleSection } from './CollapsibleSection';
import { useProject } from '../contexts/ProjectContext';
import { trackEvent } from '../utils/analytics';

export interface AssetInfoBase {
  name: string;
  file: File | null;
  url: string;
  enabled: boolean;
}

export type ResetFileInput = () => void;

export interface AssetManagementProps<TAsset extends AssetInfoBase> {
  title: string;
  uploadTitle: string;
  uploadTooltip: string;
  uploadTooltipStyle?: React.CSSProperties;
  uploadAccept: string;
  uploadAriaLabel: string;
  uploadIcon: React.ReactNode;
  onFilesSelected: (files: File[], resetFileInput: ResetFileInput) => void;

  listLabel: string;
  listTooltip: string;
  listTooltipStyle?: React.CSSProperties;
  searchPlaceholder: string;
  emptyText: string;
  noMatchText: string;

  loadingText?: string;
  loading?: boolean;
  error?: string | null;

  assets: TAsset[];
  onAssetsChange: (assets: TAsset[]) => void;

  getPrimaryStyle?: (asset: TAsset) => React.CSSProperties | undefined;
  getGroupLabel?: (asset: TAsset) => string | undefined;
  isDeletable?: (asset: TAsset) => boolean;
  deleteTitle?: string;
  onDeleteAsset?: (asset: TAsset, index: number) => void | Promise<void>;
}

type IndexedAsset<TAsset extends AssetInfoBase> = TAsset & { idx: number };

interface AssetGroup<TAsset extends AssetInfoBase> {
  label: string;
  assets: Array<IndexedAsset<TAsset>>;
}

const AssetManagement = <TAsset extends AssetInfoBase>({
  title,
  uploadTitle,
  uploadTooltip,
  uploadTooltipStyle,
  uploadAccept,
  uploadAriaLabel,
  uploadIcon,
  onFilesSelected,
  listLabel,
  listTooltip,
  listTooltipStyle,
  searchPlaceholder,
  emptyText,
  noMatchText,
  loadingText,
  loading = false,
  error = null,
  assets,
  onAssetsChange,
  getPrimaryStyle,
  getGroupLabel,
  isDeletable,
  deleteTitle,
  onDeleteAsset,
}: AssetManagementProps<TAsset>) => {
  const { selectedProject } = useProject();
  const [search, setSearch] = useState<string>('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const filteredAssets = useMemo(() => {
    const needle: string = search.toLowerCase();
    return assets
      .map((a, idx) => ({ ...a, idx }))
      .filter((a) => a.name.toLowerCase().includes(needle));
  }, [assets, search]);
  const selectedAssetNames = useMemo(
    () => assets.filter((asset) => asset.enabled).map((asset) => asset.name),
    [assets]
  );
  const groupedFilteredAssets = useMemo<Array<AssetGroup<TAsset>>>(() => {
    if (!getGroupLabel) {
      return [];
    }

    const groups = new Map<string, Array<IndexedAsset<TAsset>>>();
    for (const asset of filteredAssets) {
      const label: string = getGroupLabel(asset) || '';
      const groupAssets: Array<IndexedAsset<TAsset>> = groups.get(label) ?? [];
      groupAssets.push(asset);
      groups.set(label, groupAssets);
    }

    return Array.from(groups.entries()).map(([label, groupAssets]) => ({
      label,
      assets: groupAssets,
    }));
  }, [filteredAssets, getGroupLabel]);

  const handleOpen = (): void => {
    window.setTimeout(() => searchInputRef.current?.focus(), 60);
  };
  const stopPropagation = (e: React.SyntheticEvent): void => e.stopPropagation();

  const handleSelectAll = (): void => {
    const allSelected: boolean = filteredAssets.length > 0 && filteredAssets.every((a) => a.enabled);
    const filteredNames = new Set(filteredAssets.map((a) => a.name));
    const updated = assets.map((a) => (filteredNames.has(a.name) ? ({ ...a, enabled: !allSelected } as TAsset) : a));
    trackEvent('asset_visibility_bulk_changed', {
      project_id: selectedProject?.id,
      asset_type: title,
      visible_count: updated.filter((asset) => asset.enabled).length,
    });
    onAssetsChange(updated);
  };

  const handleToggleGroup = (groupAssets: Array<IndexedAsset<TAsset>>): void => {
    const groupIndexes = new Set(groupAssets.map((asset) => asset.idx));
    const allSelected: boolean = groupAssets.length > 0 && groupAssets.every((asset) => asset.enabled);
    const updated = assets.map((asset, index) =>
      groupIndexes.has(index) ? ({ ...asset, enabled: !allSelected } as TAsset) : asset
    );
    trackEvent('asset_visibility_group_changed', {
      project_id: selectedProject?.id,
      asset_type: title,
      visible_count: updated.filter((asset) => asset.enabled).length,
    });
    onAssetsChange(updated);
  };

  const handleToggleAsset = (idx: number): void => {
    const updated = assets.map((a, i) => (i === idx ? ({ ...a, enabled: !a.enabled } as TAsset) : a));
    trackEvent('asset_visibility_changed', {
      project_id: selectedProject?.id,
      asset_type: title,
      asset_name: assets[idx]?.name,
      enabled: !assets[idx]?.enabled,
    }, {
      dedupeKey: `${selectedProject?.id ?? 'no_project'}:${title}:${assets[idx]?.name ?? idx}`,
    });
    onAssetsChange(updated);
  };

  const handleUploadChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files: FileList | null = e.target.files;
    if (!files || files.length === 0) return;

    const reset: ResetFileInput = () => {
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    onFilesSelected(Array.from(files), reset);
  };

  const canDelete = (asset: TAsset): boolean => (isDeletable ? isDeletable(asset) : true);
  const toggleExpandedGroup = (label: string): void => {
    setExpandedGroups((previousGroups: Set<string>) => {
      const nextGroups = new Set(previousGroups);
      if (nextGroups.has(label)) {
        nextGroups.delete(label);
      } else {
        nextGroups.add(label);
      }
      return nextGroups;
    });
  };

  const renderAssetRow = (asset: IndexedAsset<TAsset>): React.ReactNode => (
    <MenuItem
      key={`${asset.name}-${asset.idx}`}
      value={asset.name}
      onClick={(e) => {
        e.stopPropagation();
        handleToggleAsset(asset.idx);
      }}
      sx={{ borderRadius: 0, display: 'flex', alignItems: 'center' }}
    >
      <Checkbox
        checked={asset.enabled}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleAsset(asset.idx);
        }}
        disableRipple
      />
      <ListItemText
        primary={asset.name}
        primaryTypographyProps={{ style: getPrimaryStyle ? getPrimaryStyle(asset) : undefined }}
      />

      {onDeleteAsset && canDelete(asset) && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteAsset(asset, asset.idx);
          }}
          sx={{ ml: 'auto', color: '#999', '&:hover': { color: '#f44336' } }}
          title={deleteTitle ?? String(t`Delete`)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </MenuItem>
  );

  return (
    <div className="font-management">
      <CollapsibleSection title={title} contentClassName="font-management-group">
          <div className="setting-item upload-section">
            <label className="upload-label">
              <span className="upload-title">{uploadTitle}</span>
              <div className="tooltip-wrapper">
                <span className="tooltip-icon" style={{ marginLeft: '4px' }}>
                  ?
                </span>
                <div className="tooltip-text" style={{ paddingTop: '10px', height: '40px', ...uploadTooltipStyle }}>
                  {uploadTooltip}
                </div>
              </div>
            </label>

            <div className="upload-controls">
              <input
                ref={fileInputRef}
                type="file"
                accept={uploadAccept}
                multiple
                onChange={handleUploadChange}
                style={{ display: 'none' }}
              />

              <Button
                variant="contained"
                onClick={() => fileInputRef.current?.click()}
                disableElevation
                disableRipple
                aria-label={uploadAriaLabel}
                sx={{
                  width: 44,
                  height: 44,
                  minWidth: 44,
                  padding: 0,
                  borderRadius: 2,
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  border: '2px solid #1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.18s ease-in-out',
                  '&:hover': { backgroundColor: '#fff', color: '#1976d2', borderColor: '#1976d2', transform: 'scale(1.05)' },
                }}
              >
                {uploadIcon}
              </Button>
            </div>

            {loading && loadingText && <div style={{ marginTop: '8px', color: '#1976d2' }}>{loadingText}</div>}
            {error && <div style={{ marginTop: '8px', color: 'red' }}>{error}</div>}
          </div>

          <div className="setting-item font-list-section">
            <span className="font-list-label">{listLabel}</span>
            <span className="tooltip-wrapper">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text-ratio" style={listTooltipStyle}>
                {listTooltip}
              </span>
            </span>

            <FormControl className="font-list-select" fullWidth>
              <Select
                sx={{ height: '40px', '& .MuiSelect-icon': { color: '#1976d2' } }}
                multiple
                value={selectedAssetNames}
                renderValue={(selected) => (
                  <Box sx={{ height: '50%', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(selected as string[]).length ? (selected as string[]).join(', ') : t`None`}
                  </Box>
                )}
                onOpen={handleOpen}
                onClose={() => setSearch('')}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 260, borderRadius: 8, overflow: 'auto' } },
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  autoFocus: false,
                  MenuListProps: { onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation() },
                }}
              >
                <MenuItem disableRipple onClick={stopPropagation} sx={{ px: 1, py: 0.5 }}>
                  <Box onMouseDown={(e) => e.stopPropagation()} sx={{ width: '100%' }}>
                    <TextField
                      inputRef={searchInputRef}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onKeyUp={(e) => e.stopPropagation()}
                      placeholder={searchPlaceholder}
                      size="small"
                      variant="standard"
                      fullWidth
                      InputProps={{ disableUnderline: true, style: { fontSize: 13, paddingLeft: 6 } }}
                    />
                  </Box>
                </MenuItem>

                {filteredAssets.length > 0 && (
                  <MenuItem
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAll();
                    }}
                    sx={{
                      marginTop: '4px',
                      marginLeft: '28px',
                      width: '75%',
                      px: 1,
                      py: 0.5,
                      fontWeight: 500,
                      cursor: 'pointer',
                      borderRadius: 1,
                      backgroundColor: '#e3f2fd',
                      '&:hover': { backgroundColor: '#bbdefb' },
                      mb: 0.5,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {filteredAssets.every((a) => a.enabled) ? t`Unselect All` : t`Select All`}
                  </MenuItem>
                )}

                {assets.length === 0 && <MenuItem disabled>{emptyText}</MenuItem>}
                {filteredAssets.length === 0 && assets.length > 0 && <MenuItem disabled>{noMatchText}</MenuItem>}

                {getGroupLabel
                  ? groupedFilteredAssets.map((group) => {
                      const isExpanded: boolean = expandedGroups.has(group.label) || search.trim().length > 0;
                      const isEveryGroupAssetEnabled: boolean = group.assets.every((asset) => asset.enabled);
                      const isSomeGroupAssetEnabled: boolean = group.assets.some((asset) => asset.enabled);
                      return (
                        <React.Fragment key={group.label}>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpandedGroup(group.label);
                            }}
                            sx={{
                              borderRadius: 0,
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: '#f7fafc',
                              fontWeight: 700,
                            }}
                          >
                            <Checkbox
                              checked={isEveryGroupAssetEnabled}
                              indeterminate={!isEveryGroupAssetEnabled && isSomeGroupAssetEnabled}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleGroup(group.assets);
                              }}
                              disableRipple
                            />
                            <ListItemText
                              primary={group.label}
                              secondary={`${group.assets.filter((asset) => asset.enabled).length}/${group.assets.length}`}
                              primaryTypographyProps={{ style: { fontWeight: 700 } }}
                              secondaryTypographyProps={{ style: { fontSize: '11px' } }}
                            />
                            <span aria-hidden="true" style={{ color: '#4a5568', fontWeight: 700 }}>
                              {isExpanded ? 'v' : '>'}
                            </span>
                          </MenuItem>
                          {isExpanded && group.assets.map((asset) => renderAssetRow(asset))}
                        </React.Fragment>
                      );
                    })
                  : filteredAssets.map((asset) => renderAssetRow(asset))}
              </Select>
            </FormControl>
          </div>
      </CollapsibleSection>
    </div>
  );
};

export default AssetManagement;
